import React, { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { MdOpenInNew, MdExpandMore } from 'react-icons/md';
import { useEntitlements } from '@hooks/useEntitlements';
import { useSidebar } from '@contexts/useSidebar';
import { GATE } from '@api/types/entitlements';
import {
  Container,
  Trigger,
  TriggerLabel,
  TriggerContent,
  ChevronIcon,
  Dropdown,
  DropdownItem,
  Divider,
  Overlay,
} from './ProductSwitcher.styled';

/**
 * CAM frontend URL — opened directly in a new tab.
 * Authentication relies on the shared SSO cookie (`aibuild_token`) that OS
 * sets on `.ai-build.com` at login time (see `sessionCookie.ts`). No token
 * is passed in the URL; the browser automatically sends the `aibuild_token`
 * cookie with the request and the CAM frontend uses that cookie to
 * bootstrap / exchange it for a session.
 *
 * Set via VITE_CAM_URL env var. When unset, "Switch to CAM" is hidden.
 */
const CAM_URL = import.meta.env.VITE_CAM_URL || '';

interface Product {
  id: string;
  label: string;
  /** If set, clicking this product opens this URL in a new tab */
  url?: string;
}

interface ProductSwitcherProps {
  dataTestId?: string;
}

const ProductSwitcher: React.FC<ProductSwitcherProps> = ({
  dataTestId = 'product-switcher',
}) => {
  const intl = useIntl();
  // Open state lives on `SidebarContext` (not local) so the editor's
  // collapsed rail can flip it to `true` BEFORE this component mounts:
  // the rail unmounts the entire `NavigationBar` (and this switcher),
  // and clicking the rail's "OS" button both expands the rail (which
  // re-mounts us) AND opens the dropdown in the same render. Local
  // state would be lost across that mount/unmount cycle.
  const {
    isProductSwitcherOpen: isOpen,
    closeProductSwitcher,
    toggleProductSwitcher,
  } = useSidebar();
  const [activeProductId] = useState('aibuild-os');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isGateOpen } = useEntitlements();

  const currentProductLabel = intl.formatMessage({
    id: 'productSwitcher.aibuildOs',
    defaultMessage: 'Aibuild OS',
  });

  const PRODUCTS: Product[] = useMemo(
    () => {
      const items: Product[] = [
        { id: 'aibuild-os', label: currentProductLabel },
      ];
      if (isGateOpen(GATE.CAM) && CAM_URL) {
        items.push({
          id: 'aibuild-cam',
          label: intl.formatMessage({ id: 'productSwitcher.aibuildCam', defaultMessage: 'Aibuild CAM' }),
          url: CAM_URL,
        });
      }
      return items;
    },
    [intl, isGateOpen, currentProductLabel],
  );

  const activeProduct = PRODUCTS.find((p) => p.id === activeProductId);
  const otherProducts = PRODUCTS.filter((p) => p.id !== activeProductId);

  const handleToggle = toggleProductSwitcher;
  const handleClose = closeProductSwitcher;

  const handleSelect = useCallback(
    (product: Product) => {
      closeProductSwitcher();

      if (product.url) {
        window.open(product.url, '_blank', 'noopener,noreferrer');
      }
    },
    [closeProductSwitcher],
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeProductSwitcher();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeProductSwitcher]);

  // Close the dropdown if this component is unmounting (e.g. the
  // editor's rail collapses and unmounts NavigationBar). Otherwise the
  // open flag stays `true` in context and would re-open spuriously the
  // next time NavigationBar mounts. The empty-deps cleanup guarantees
  // this fires once on unmount, never on regular re-renders.
  useEffect(() => () => closeProductSwitcher(), [closeProductSwitcher]);

  return (
    <Container ref={dropdownRef} data-testid={dataTestId}>
      <Trigger
        type="button"
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={intl.formatMessage({
          id: 'productSwitcher.ariaLabel',
          defaultMessage: 'Switch product',
        })}
      >
        <TriggerContent>
          <TriggerLabel>{currentProductLabel}</TriggerLabel>
        </TriggerContent>
        <ChevronIcon $isOpen={isOpen}>
          <MdExpandMore size={20} />
        </ChevronIcon>
      </Trigger>

      {isOpen && (
        <>
          <Overlay onClick={handleClose} />
          <Dropdown
            role="listbox"
            aria-label={intl.formatMessage({
              id: 'productSwitcher.dropdownLabel',
              defaultMessage: 'Product switcher',
            })}
          >
            {activeProduct && (
              <DropdownItem
                type="button"
                $isActive
                role="option"
                aria-selected
                onClick={() => handleSelect(activeProduct)}
              >
                {activeProduct.label}
              </DropdownItem>
            )}

            {otherProducts.length > 0 && <Divider />}

            {otherProducts.map((product) => (
              <DropdownItem
                key={product.id}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => handleSelect(product)}
              >
                {product.label}
                {product.url && <MdOpenInNew size={14} />}
              </DropdownItem>
            ))}
          </Dropdown>
        </>
      )}
    </Container>
  );
};

export default ProductSwitcher;
